import RouterUtil from '@/utils/RouterUtil';
import AuthService from '@/services/AuthService';
import { remoteConfig } from '@/services';
import SupplierService from '@/services/SupplierService';
import SellerTag from '@/models/SellerTag';
import sso from '@/router/sso';

export default [
  {
    path: '/account',
    component: () => import(/* webpackChunkName: "account" */ '@/views/Account/Public.vue'),
    beforeEnter: (to, from, next) => {
      const { meta } = to;

      if (AuthService.isAuthenticated() && (meta.enterAuthUser)) {
        AuthService.logout();

        return next();
      }

      if (AuthService.isAuthenticated() && !meta.requiresAuth) {
        window.location = from.path;
        return;
      }

      return next();
    },
    children: [
      {
        path: 'login',
        name: 'Login',
        meta: {
          requiresAuth: false,
          isNewDesign: true,
        },
        component: () => import(/* webpackChunkName: "login" */ '@/views/LoginNew/Index'),
      },
      {
        path: 'forgot-password',
        name: 'Forgot',
        meta: {
          requiresAuth: false,
          isNewDesign: true,
        },
        component: remoteConfig.getConfigValueByKey('IS_NEW_RESET_PASSWORD')
          ? () => import(/* webpackChunkName: "forgot" */ '@/views/ForgotPassword/Index.vue')
          : () => import(/* webpackChunkName: "forgot" */ '@/views/Forgot/Index.vue'),
      },
      {
        path: 'reset-password',
        name: 'Reset',
        meta: {
          requiresAuth: false,
          enterAuthUser: true,
          isNewDesign: true,
        },
        beforeEnter: (to, from, next) => {
          if (to.query.token || to.query.phoneNumberToken || to.query.onboardingToken) {
            return next({ name: 'Reset', params: to.query });
          } else if (to.params.token || to.params.phoneNumberToken || to.params.onboardingToken) {
            return next();
          }
          return next({ name: 'Login' });
        },
        component: () => import(/* webpackChunkName: "reset" */ '@/views/Reset/Index.vue'),
      },
      {
        path: 'logout',
        name: 'Logout',
        meta: { requiresAuth: false, enterAuthUser: true },
        component: () => import(/* webpackChunkName: "info" */ '@/views/Logout/Index.vue'),
      },
      {
        path: 'verify-email',
        name: 'VerifyEmail',
        meta: {
          requiresAuth: false,
          enterAuthUser: true,
          isNewDesign: true,
          withoutHeader: true,
        },
        component: () => import(/* webpackChunkName: "verifyEmail" */ '@/views/VerifyEmail/Index.vue'),
        beforeEnter: (to, from, next) => {
          const { token } = to.query;

          if (token) {
            delete to.query.token;
            to.params.token = token;

            return next({
              ...to,
              query: to.query,
            });
          }

          next();
        },
      },
    ],
  },
  {
    path: '/account',
    component: () => import(/* webpackChunkName: "account" */ '@/views/Account/Private.vue'),
    beforeEnter: (to, from, next) => {
      const { meta } = to;

      if (AuthService.isAuthenticated() && (meta.enterAuthUser)) {
        AuthService.logout();

        return next();
      }

      return next();
    },
    children: [
      {
        path: '/',
        redirect: {
          name: 'Info',
        },
      },
      {
        path: 'info',
        name: 'Info',
        meta: { requiresAuth: true },
        component: () => import(/* webpackChunkName: "info" */ '@/views/Info/Index.vue'),
        beforeEnter: (to, from, next) => {
          const hideAccountPages = remoteConfig.getConfigValueByKey('HIDE_ACCOUNT_PAGES');

          if (hideAccountPages) {
            window.location.href = '/dashboard';

            return false;
          }

          const { unsubscribe, phoneVerificationRequired } = to.query;

          if (typeof phoneVerificationRequired !== 'undefined') {
            delete to.query.phoneVerificationRequired;
            to.params.phoneVerificationRequired = true;

            return next({
              ...to,
              query: to.query,
            });
          }
          if (unsubscribe) {
            delete to.query.unsubscribe;
            to.params.unsubscribe = true;

            return next({
              ...to,
              query: to.query,
            });
          }

          next();
        },
      },
      {
        path: 'authorization',
        name: 'Authorization',
        meta: { requiresAuth: true },
        component: () => import(/* webpackChunkName: "authorization" */ '@/views/Authorization/Index.vue'),
        beforeEnter: RouterUtil.hasAccess('ROLE_ADMIN', () => {
          const hideAccountPages = remoteConfig.getConfigValueByKey('HIDE_ACCOUNT_PAGES');

          if (hideAccountPages) {
            window.location.href = '/dashboard';
          }

          return true;
        }),
      },
      ...sso,
      {
        path: 'seller-reference',
        name: 'SellerReference',
        meta: { requiresAuth: true },
        component: () => import('@/views/SellerReference/Index.vue'),
        beforeEnter: (to, from, next) => {
          const isMarkedSeller = SupplierService.hasTag(parseInt(SellerTag.KA1)) || SupplierService.hasTag(parseInt(SellerTag.KA2));
          const isEnabled = remoteConfig.getConfigValueByKey('IS_SELLER_REFERENCE', false);

          if (isMarkedSeller || !isEnabled) {
            window.location.href = '/dashboard';

            return false;
          }

          return next();
        },
      },
    ],
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import(/* webpackChunkName: "info" */ '@/views/Unauthorized/Index.vue'),
  },
  {
    path: '/unauthorized/dashboard',
    name: 'Dashboard',
    component: () => import(/* webpackChunkName: "info" */ '@/views/Unauthorized/Index.vue'),
  },
];
